'use client'

import Image from 'next/image'
import { useState, useRef, useEffect } from 'react'
import { useLocale } from 'next-intl'
import { useRouter, usePathname } from '@/i18n/navigation'
import { useParams } from 'next/navigation'
import type { Locale } from '@/i18n/routing'

const languages = [
  { code: 'tr' as Locale, label: 'Türkçe', flag: '/tr.svg' },
  { code: 'en' as Locale, label: 'English', flag: '/us.svg' },
]

export default function LanguageSwitcher() {
  const locale = useLocale() as Locale
  const router = useRouter()
  const pathname = usePathname()
  const routeParams = useParams()
  const [open, setOpen] = useState(false)
  const [closing, setClosing] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const selected = languages.find(l => l.code === locale) ?? languages[0]

  const handleClose = () => {
    setClosing(true)
    setTimeout(() => {
      setOpen(false)
      setClosing(false)
    }, 200)
  }

  const switchLang = (newLang: Locale) => {
    if (newLang === locale) { handleClose(); return }
    // Route params'tan locale'i çıkar, sadece dinamik params kalsın (slug, id, pair, symbol vs.)
    const { locale: _locale, ...params } = routeParams
    router.replace(
      // @ts-expect-error - pathname comes from current route, it's valid
      { pathname, params },
      { locale: newLang }
    )
    handleClose()
  }

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        if (open) handleClose()
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => open ? handleClose() : setOpen(true)}
        className="flex items-center gap-2 h-9 pl-2 pr-2.5 rounded-full cursor-pointer transition-all duration-200"
        style={{
          background: open ? 'var(--bg-body)' : 'transparent',
          border: 'none',
          color: 'var(--text-main)',
        }}
      >
        <Image src={selected.flag} alt={selected.label} width={22} height={16} className="country-flag" />
        <span className="text-[13px] font-semibold uppercase tracking-wide">{selected.code}</span>
        <svg
          width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          style={{
            color: 'var(--text-muted)',
            transition: 'transform 0.2s ease',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute top-full right-0 mt-2 w-48 rounded-xl border overflow-hidden z-50"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-color)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
            opacity: closing ? 0 : 1,
            transform: closing ? 'translateY(-6px) scale(0.97)' : 'translateY(0) scale(1)',
            transition: 'opacity 0.2s ease, transform 0.2s ease',
          }}
        >
          {languages.map((l) => {
            const isActive = locale === l.code
            return (
              <button
                key={l.code}
                onClick={() => switchLang(l.code)}
                className={`flex items-center gap-3 w-full px-3.5 py-3 border-none cursor-pointer transition-all duration-150 text-left ${isActive ? '' : 'hover:bg-[var(--bg-body)]'}`}
                style={{
                  background: isActive ? 'var(--bg-body)' : 'transparent',
                  color: 'var(--text-main)',
                }}
              >
                <Image src={l.flag} alt={l.label} width={26} height={19} className="country-flag" />
                <span className="text-sm font-medium flex-1">{l.label}</span>
                {isActive && (
                  <div
                    className="w-5 h-5 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: 'var(--secondary-color)' }}
                  >
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
